import React, { useState, useEffect } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import type { Location } from 'react-router-dom';
import './AnimatedRoutes.css';

interface RoutesProps {
  location?: Location;
}

interface AnimatedRoutesProps {
  children: React.ReactElement<RoutesProps>;
}

interface AnimInfo {
  direction: string;
  entering: 'A' | 'B';
}

interface SlotState {
  prevKey: string | undefined;
  slotA: Location;
  slotB: Location;
  active: 'A' | 'B';
  anim: AnimInfo | null;
}

/**
 * 2スロット方式のページ遷移アニメーション。
 *
 * slot A / slot B を交互に使用し、CSSクラスの切り替えのみでアニメーションを実現。
 * 各スロットのReact要素ツリーは維持されるため、remountによる白フラッシュが発生しない。
 *
 * - 通常時: activeスロットのみ表示、もう一方は display:none
 * - アニメーション中: 両スロットを absolute で重ねて表示
 * - アニメーション終了: CSSクラスのみ変更（DOM構造変更なし）
 */
const AnimatedRoutes: React.FC<AnimatedRoutesProps> = ({ children }) => {
  const location = useLocation();
  const navigationType = useNavigationType();

  const [state, setState] = useState<SlotState>({
    prevKey: location.key,
    slotA: location,
    slotB: location,
    active: 'A',
    anim: null,
  });

  // レンダー中にlocation変更を同期検知（setState during render）
  if (location.key !== state.prevKey) {
    if (navigationType === 'REPLACE') {
      setState(prev => ({
        ...prev,
        prevKey: location.key,
        ...(prev.active === 'A' ? { slotA: location } : { slotB: location }),
      }));
    } else {
      const entering = state.active === 'A' ? 'B' : 'A';
      const dir = navigationType === 'POP' ? 'slide-back' : 'slide-forward';
      setState({
        prevKey: location.key,
        slotA: entering === 'A' ? location : state.slotA,
        slotB: entering === 'B' ? location : state.slotB,
        active: entering,
        anim: { direction: dir, entering },
      });
    }
  }

  // アニメーション終了タイマー
  useEffect(() => {
    if (!state.anim) return;
    const timer = setTimeout(() => {
      setState(prev => ({ ...prev, anim: null }));
    }, 370);
    return () => clearTimeout(timer);
  }, [state.anim]);

  const { anim, active } = state;
  const isAnimating = !!anim;

  const getSlotClass = (slot: 'A' | 'B'): string => {
    if (isAnimating) {
      const isEntering = anim!.entering === slot;
      return `route-transition-page-animating ${anim!.direction}-${isEntering ? 'enter' : 'exit'}`;
    }
    return slot === active ? '' : 'route-transition-hidden';
  };

  return (
    <div className={`route-transition-container${isAnimating ? ' animating' : ''}`}>
      <div key="A" className={getSlotClass('A')}>
        {React.cloneElement(children, { location: state.slotA })}
      </div>
      <div key="B" className={getSlotClass('B')}>
        {React.cloneElement(children, { location: state.slotB })}
      </div>
    </div>
  );
};

export default AnimatedRoutes;
